import React, { useMemo, useState } from "react";
import { AsYouType, getCountries, getCountryCallingCode } from "libphonenumber-js";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from "./ui/command";
import { Input } from "./ui/input";
import { ChevronDown } from "lucide-react";

/** ISO country code -> flag emoji (regional indicator symbols, no assets needed) */
const flagOf = (iso) =>
  iso.toUpperCase().replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));

// Most relevant first, then everything else alphabetically
const PRIORITY = ["US", "BR", "PT", "MX", "CA", "AR", "CO", "ES"];
const ALL_COUNTRIES = (() => {
  const names = new Intl.DisplayNames(["en"], { type: "region" });
  const list = getCountries().map((iso) => ({
    iso,
    name: (() => { try { return names.of(iso) || iso; } catch (e) { return iso; } })(),
    dial: getCountryCallingCode(iso),
  }));
  const prio = PRIORITY.map((p) => list.find((c) => c.iso === p)).filter(Boolean);
  const rest = list.filter((c) => !PRIORITY.includes(c.iso)).sort((a, b) => a.name.localeCompare(b.name));
  return [...prio, ...rest];
})();

/**
 * International phone input: country selector (flag + dial code) + as-you-type
 * mask. Emits the FULL number (+<dial><digits>) via onChange.
 */
export const PhoneInput = ({ value, onChange, placeholder, className = "", dataTestId = "phone-input" }) => {
  const [country, setCountry] = useState("US");
  const [open, setOpen] = useState(false);
  const dial = useMemo(() => getCountryCallingCode(country), [country]);

  // Local part (national number) shown in the text box, formatted
  const national = useMemo(() => {
    const v = (value || "").replace(`+${dial}`, "");
    return new AsYouType(country).input(v);
  }, [value, dial, country]);

  const handleText = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "");
    onChange(digits ? `+${dial}${digits}` : "");
  };

  const pick = (iso) => {
    setCountry(iso);
    setOpen(false);
    // Re-emit with the new dial code, keeping the typed national digits
    const digits = (value || "").replace(/^\+\d{1,3}/, "").replace(/[^\d]/g, "");
    const newDial = getCountryCallingCode(iso);
    onChange(digits ? `+${newDial}${digits}` : "");
  };

  const current = ALL_COUNTRIES.find((c) => c.iso === country);

  return (
    <div className={`flex items-stretch gap-1.5 ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`${dataTestId}-country`}
            className="h-11 shrink-0 inline-flex items-center gap-1 rounded-lg border border-input bg-background px-2.5 text-sm hover:border-[#2B3990]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="text-base leading-none">{flagOf(country)}</span>
            <span className="text-xs text-muted-foreground font-medium">+{dial}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-64" align="start">
          <Command>
            <CommandInput placeholder={current ? current.name : "Search..."} data-testid={`${dataTestId}-country-search`} />
            <CommandEmpty>—</CommandEmpty>
            <CommandList className="max-h-56">
              {ALL_COUNTRIES.map((c) => (
                <CommandItem key={c.iso} value={`${c.name} ${c.iso} +${c.dial}`} onSelect={() => pick(c.iso)} data-testid={`${dataTestId}-country-${c.iso}`}>
                  <span className="mr-2">{flagOf(c.iso)}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-xs text-muted-foreground">+{c.dial}</span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input
        type="tel"
        inputMode="tel"
        data-testid={dataTestId}
        placeholder={placeholder}
        value={national}
        onChange={handleText}
        className="h-11 flex-1"
      />
    </div>
  );
};
